import React, { useCallback, useState } from 'react';
import { ControlLink, Text, Overlay } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useAddressesQuery, RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { LoadingBalls } from '../../LoadingBalls';
import { PayPalButton } from '../../PaymentSourceButton';
import { PayPalShippingAddressPreview } from '../PayPalShippingAddressPreview';
import { PayPalPaymentMethodPreview } from '../PayPalPaymentMethodPreview';
import './PayPalShippingAndPayment.scss';

type Props = {
  theme?: 'box' | 'simple';
  paypalClientToken?: string | null;
  address?: RestAddress;
  addressId?: number | null;
  readOnly?: boolean;
  onSwitchToAdyen?: () => Promise<void>;
  onSubmit?: () => Promise<void>;
};

enum Mode {
  Resting,
  Switching
}

export const PayPalShippingAndPayment: React.FC<Props> = ({
  theme = 'simple',
  paypalClientToken,
  address,
  addressId,
  readOnly,
  onSwitchToAdyen,
  onSubmit
}) => {
  const { showErrorModal } = useErrorModal();
  const { addresses, error: addressesError } = useAddressesQuery({ skip: address != null });
  const [mode, setMode] = useState(Mode.Resting);

  const handleSwitchToAdyen = useCallback(async () => {
    setMode(Mode.Switching);
    try {
      await onSwitchToAdyen?.();
    } catch (error) {
      reportError(error);
      showErrorModal();
    }
    setMode(Mode.Resting);
  }, [setMode, onSwitchToAdyen, showErrorModal]);

  const selectedAddress = address || addresses.find(address => address.id === String(addressId));

  if (addressesError) throwQueryError(addressesError);

  if (!selectedAddress) return null;

  return (
    <Box borderless={theme === 'simple'}>
      <div className="PayPalShippingAndPayment">
        <div className="PayPalShippingAndPayment__section">
          <PayPalShippingAddressPreview address={selectedAddress} />
        </div>
        <div className="PayPalShippingAndPayment__section">
          <PayPalPaymentMethodPreview />
        </div>

        {!readOnly && (
          <div className="PayPalShippingAndPayment__actions">
            <ControlLink onClick={handleSwitchToAdyen}>
              I do not want to pay with PayPal
            </ControlLink>
            <PayPalButton
              size="small"
              paypalClientToken={paypalClientToken ?? null}
              onSelect={onSubmit}
            />
          </div>
        )}

        <Overlay className="PayPalShippingAndPayment__overlay" show={mode === Mode.Switching}>
          <LoadingBalls />
          <Text treatment="eyebrow">Switching</Text>
        </Overlay>
      </div>
    </Box>
  );
};
